import React, { useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@material-ui/core';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/ExpertReport/Decision/messages';
import { ExpertReportDecisionDialogWrapper } from 'containers/CourtManagerLegalProceedings/ExpertReport/styled-components';
import { useContestValidation } from 'containers/CourtManagerLegalProceedings/ExpertReport/Decision/hook';
import UploadDocumentBloc from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/UploadDocumentBloc';
import { DOCUMENT_TYPES } from 'constants/global';
import {
  IntlMessage,
  IUploadedDocument,
} from 'containers/LawyerLegalProceedingsPage/typings';
import { CorporealDocument } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import {
  IVehiculeExpertise,
  IVictimExpertise,
} from 'containers/common/LegalProceedings/ExpertReport/typings';
import {
  PROCEDURE_STATUSES,
  VEHICULE_EXPERTISE_FIELDS,
  VICTIM_EXPERTISE_FIELDS,
} from 'containers/data-structure';
import { cloneDeep, uniqueId } from 'lodash';
import {
  DECISION,
  DECISION_STATUS,
  EMPTY_DECISION,
} from 'containers/CourtManagerLegalProceedings/ExpertReport/constants';
import { IExpertReportDecision } from 'containers/CourtManagerLegalProceedings/ExpertReport/typings';

export interface ExpertReportDecisionDialogProps {
  intl: IntlShape;
  handleCancel: Function;
  isOpen: boolean;
  uploadDocument: Function;
  uploadedDocument: IUploadedDocument;
  resetUploadedDocument: Function;
  expertise: IVictimExpertise | IVehiculeExpertise;
  setExpertReport: Function;
  setSelectedExpertise: Function;
  isExpertReportValidated: boolean;
}

export const ExpertReportDecisionDialog: React.FC<ExpertReportDecisionDialogProps> = (
  props,
): JSX.Element => {
  const [decision, setDecision] = useState<IExpertReportDecision>(
    cloneDeep(EMPTY_DECISION),
  );

  const [canValidate] = useContestValidation(decision);

  const handleChangeDecision = (
    event: React.ChangeEvent<{
      value: unknown;
      name?: string;
    }>,
  ) => {
    const { name, value } = event.target;
    const expertDecision = { ...decision };

    expertDecision[name as string] = value as string;

    if (expertDecision[DECISION.DECISION] !== PROCEDURE_STATUSES.CONTESTED) {
      expertDecision[DECISION.CONTEST_REASON] = null;
    }
    if (expertDecision[DECISION.DECISION] !== PROCEDURE_STATUSES.REJECTED) {
      expertDecision[DECISION.REJECTION_REASON] = '';
    }

    setDecision(expertDecision);
  };
  const handleSetDocument = (document: CorporealDocument) => {
    const expertDecision = { ...decision };

    expertDecision[DECISION.DOCUMENT] = document;

    setDecision(expertDecision);
  };

  const onValidate = () => {
    const victimReport = { ...props.expertise } as IVictimExpertise;
    const vehiculeReport = { ...props.expertise } as IVehiculeExpertise;

    if (!!victimReport.victimExpertise) {
      const { victimExpertise } = victimReport;

      victimExpertise[VICTIM_EXPERTISE_FIELDS.CONSULTANCY_REPORT_DOCUMENT] = {
        ...decision[DECISION.DOCUMENT],
        disputeReason: decision[DECISION.CONTEST_REASON],
        victimExpertiseId: victimExpertise[VEHICULE_EXPERTISE_FIELDS.ID],
      };
      victimExpertise[VICTIM_EXPERTISE_FIELDS.STATUS] =
        decision[DECISION.DECISION];
      victimExpertise[VICTIM_EXPERTISE_FIELDS.REJECTION_REASON] =
        decision[DECISION.REJECTION_REASON];
      victimExpertise[VICTIM_EXPERTISE_FIELDS.HAS_DECISION] = true;
      props.setExpertReport(victimReport);
    }

    if (!!vehiculeReport.vehiculeExpertise) {
      const { vehiculeExpertise } = vehiculeReport;

      vehiculeExpertise[
        VEHICULE_EXPERTISE_FIELDS.CONSULTANCY_REPORT_DOCUMENT
      ] = {
        ...decision[DECISION.DOCUMENT],
        disputeReason: decision[DECISION.CONTEST_REASON],
        vehiculeExpertiseId: vehiculeExpertise[VEHICULE_EXPERTISE_FIELDS.ID],
      };
      vehiculeExpertise[VEHICULE_EXPERTISE_FIELDS.STATUS] =
        decision[DECISION.DECISION];
      vehiculeExpertise[VEHICULE_EXPERTISE_FIELDS.REJECTION_REASON] =
        decision[DECISION.REJECTION_REASON];
      vehiculeExpertise[VICTIM_EXPERTISE_FIELDS.HAS_DECISION] = true;

      props.setExpertReport(vehiculeReport);
    }

    onCancel();
  };

  const onCancel = () => {
    setDecision(cloneDeep(EMPTY_DECISION));
    props.setSelectedExpertise({});
    props.handleCancel(false);
  };

  const decisionListing = [
    messages.confirm,
    messages.dismiss,
    messages.contest,
  ];
  return (
    <ExpertReportDecisionDialogWrapper
      className="refusal-content"
      handleValidate={onValidate}
      handleClose={onCancel}
      title={translate(messages.contestDialogTitle, props)}
      cancelTitle={translate(messages.contestDialogCancel, props)}
      confirmTitle={translate(messages.contestDialogValidate, props)}
      open={props.isOpen}
      validateButtonDisabled={!canValidate}
    >
      <div className="contest-wrapper">
        <FormControl fullWidth>
          <InputLabel>{translate(messages.selectDecision, props)}</InputLabel>
          <Select
            name={DECISION.DECISION}
            value={decision[DECISION.DECISION]}
            onChange={handleChangeDecision}
            label={translate(messages.selectDecision, props)}
          >
            {decisionListing.map((item: IntlMessage) => {
              return (
                <MenuItem
                  value={DECISION_STATUS[item.id]?.status}
                  key={uniqueId('decision-')}
                  disabled={
                    DECISION_STATUS[item.id]?.disabled
                      ? !props.isExpertReportValidated
                      : false
                  }
                >
                  {translate(item, props)}
                </MenuItem>
              );
            })}
          </Select>
          {decision[DECISION.DECISION] === PROCEDURE_STATUSES.REJECTED && (
            <div className="reason-wrapper">
              <TextField
                name={DECISION.REJECTION_REASON}
                label={translate(messages.rejectionReasonLabel, props)}
                onChange={handleChangeDecision}
                value={decision[DECISION.REJECTION_REASON]}
                fullWidth
              />
            </div>
          )}
        </FormControl>
        <UploadDocumentBloc
          docType={DOCUMENT_TYPES.CONSULTANCY_REPORT}
          document={decision.document as CorporealDocument}
          index={0}
          setDocument={handleSetDocument}
          uploadDocument={props.uploadDocument}
          uploadedDocument={props.uploadedDocument}
          resetUploadedDocument={props.resetUploadedDocument}
        />
        {decision[DECISION.DECISION] === PROCEDURE_STATUSES.CONTESTED && (
          <TextField
            id="expert-report-contest-reason"
            name={DECISION.CONTEST_REASON}
            label={translate(messages.contestReason, props)}
            onChange={handleChangeDecision}
            value={decision[DECISION.CONTEST_REASON]}
            fullWidth
          />
        )}
      </div>
    </ExpertReportDecisionDialogWrapper>
  );
};

export default injectIntl(ExpertReportDecisionDialog);
